import { Email, Person, Phone, Star, Visibility } from '@mui/icons-material';
import { Box, Chip, Tooltip } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import InvestmentEntityContact from '../../models/InvestmentEntityContact';
import { StyledIconButton } from '../styles/StyledButtons';
import { StyledAvatar, StyledCard, StyledCardContent, StyledTypography } from '../styles/styles';

interface EntityManagementContactCardProps {
  contact: InvestmentEntityContact;
  onPromoteToOwner?: (contactId: string) => void;
}

/** Compact contact card for entity management detail view */
export const EntityManagementContactCard: React.FC<EntityManagementContactCardProps> = ({
  contact,
  onPromoteToOwner,
}) => {
  const navigate = useNavigate();

  const fullName =
    contact.contact.name || `${contact.contact.firstName} ${contact.contact.lastName}`;

  return (
    <Box
      sx={{
        flex: '1 1 300px',
        minWidth: 260,
        maxWidth: 360,
      }}
    >
      <StyledCard tone="default" sx={{ mb: 1, height: '100%' }}>
        <StyledCardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <StyledAvatar tone="tone1" sx={{ width: 28, height: 28 }}>
              <Person sx={{ fontSize: 16 }} />
            </StyledAvatar>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  minWidth: 0,
                }}
              >
                <StyledTypography
                  fontWeight={600}
                  fontSize="0.875rem"
                  sx={{
                    lineHeight: 1.2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                  }}
                  title={fullName}
                >
                  {fullName}
                </StyledTypography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  gap: 0.75,
                  alignItems: 'center',
                  mt: 0.5,
                  flexWrap: 'wrap',
                }}
              >
                <Chip
                  label={contact.role}
                  size="small"
                  color={contact.role === 'OWNER' ? 'success' : 'primary'}
                  variant={contact.role === 'OWNER' ? 'filled' : 'outlined'}
                />
                {contact.representativeType && (
                  <Chip
                    label={
                      contact.representativeType === 'OTHER'
                        ? contact.otherRepresentativeType
                        : contact.representativeType
                    }
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5, mt: 0.75, flexWrap: 'wrap' }}>
                {contact.contact.email && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      minWidth: 0,
                    }}
                  >
                    <Email sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <StyledTypography
                      fontSize="0.75rem"
                      sx={{
                        color: 'text.secondary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: 220,
                      }}
                      title={contact.contact.email}
                    >
                      {contact.contact.email}
                    </StyledTypography>
                  </Box>
                )}
                {contact.contact.mobile && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Phone sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <StyledTypography fontSize="0.75rem" sx={{ color: 'text.secondary' }}>
                      {contact.contact.mobile}
                    </StyledTypography>
                  </Box>
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {onPromoteToOwner && contact.role !== 'OWNER' && (
                <Tooltip title="Promote to Owner">
                  <StyledIconButton
                    size="small"
                    onClick={() => onPromoteToOwner(contact.contactId)}
                  >
                    <Star />
                  </StyledIconButton>
                </Tooltip>
              )}
              <Tooltip title="View Contact">
                <StyledIconButton
                  size="small"
                  onClick={() => navigate(`/contacts/${contact.contact.id}`)}
                >
                  <Visibility fontSize="small" />
                </StyledIconButton>
              </Tooltip>
            </Box>
          </Box>
        </StyledCardContent>
      </StyledCard>
    </Box>
  );
};
